import { supabase } from '../client';
import { B2B_CANDIDATE_RESUME_BUCKET } from '../storageConfig';
import { Candidate } from '@core/types';
import type { StructuredResume } from '@modules/smart-match/types';

export type CandidateSortColumn =
  | 'created_at'
  | 'full_name'
  | 'email'
  | 'current_title'
  | 'current_company'
  | 'location'
  | 'source_channel';

export interface CandidateListOptions {
  search?: string;
  page?: number;
  pageSize?: number;
  /** Server-side sort (default `created_at` descending). */
  sortBy?: CandidateSortColumn;
  sortAsc?: boolean;
}

export interface CandidateListResult {
  data: Candidate[];
  total: number;
}

export const candidateService = {
  async list(
    orgId: string,
    searchOrOptions?: string | CandidateListOptions
  ): Promise<Candidate[] | CandidateListResult> {
    const options: CandidateListOptions =
      typeof searchOrOptions === 'string' ? { search: searchOrOptions } : searchOrOptions ?? {};

    const sortCol = options.sortBy ?? 'created_at';
    const sortAsc = options.sortAsc ?? (sortCol === 'created_at' ? false : true);

    let query = supabase
      .from('ggj_b2b_candidates')
      .select('*', { count: options.pageSize ? 'exact' : undefined })
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order(sortCol, { ascending: sortAsc });

    if (options.search) {
      query = query.or(
        `full_name.ilike.%${options.search}%,email.ilike.%${options.search}%,current_company.ilike.%${options.search}%`
      );
    }

    if (options.pageSize != null) {
      const page = Math.max(1, options.page ?? 1);
      const from = (page - 1) * options.pageSize;
      const to = from + options.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    if (options.pageSize != null) {
      return { data: data ?? [], total: count ?? 0 };
    }
    return data ?? [];
  },

  async getById(id: string): Promise<Candidate | null> {
    // `.maybeSingle()` (not `.single()`) so a missing row is a clean
    // `null` return instead of a PostgREST 406. The breadcrumb hook
    // and right-rail preview both tolerate null; a 406 spams DevTools
    // and breaks `useQuery.isError` heuristics upstream.
    const { data } = await supabase
      .from('ggj_b2b_candidates')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    return data ?? null;
  },

  async create(payload: Omit<Candidate, 'id' | 'created_at' | 'updated_at'>): Promise<Candidate> {
    const { data, error } = await supabase
      .from('ggj_b2b_candidates')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Phase 8 · C-44 — "Save to pool" for a SourcingResult surfaced by an
   * agent connector tool.
   *
   * Dedup is by (org_id, external_source, external_id) — the partial
   * index on `ggj_b2b_candidates` (migration 108) makes this lookup
   * cheap even with a large pool. When a match is found we return
   * `{ action: 'existing', candidate }` so the caller can toast
   * "Already in your pool" and link the existing row instead of
   * creating a duplicate.
   *
   * Every row created this way gets:
   *   - `sourced_by_agent = true` (C-43 migration column)
   *   - `external_source / external_id` set from the connector result
   *   - `source = 'api'` + `source_channel = 'api'` so reporting rollups
   *     treat it as an integration origin.
   */
  async createFromExternal(
    input: {
      orgId: string;
      externalSource: 'linkedin' | 'naukri' | 'github' | 'gitlab' | 'discord' | 'internal';
      externalId: string;
      name: string;
      headline?: string | null;
      profileUrl?: string | null;
      locationText?: string | null;
      avatarUrl?: string | null;
      skills?: string[] | null;
      createdByOrgUserId?: string | null;
    },
  ): Promise<{ action: 'created' | 'existing'; candidate: Candidate }> {
    if (!input.orgId) throw new Error('orgId is required.');
    if (!input.externalId) throw new Error('externalId is required.');

    // Dedup probe — the unique-ish (org_id, external_source, external_id)
    // tuple is enforced at the application layer (the partial index is
    // not UNIQUE because legacy rows lack the provenance columns).
    const { data: existing } = await supabase
      .from('ggj_b2b_candidates')
      .select('*')
      .eq('org_id', input.orgId)
      .eq('external_source', input.externalSource)
      .eq('external_id', input.externalId)
      .maybeSingle();

    if (existing) {
      return { action: 'existing', candidate: existing as Candidate };
    }

    // Split the SourcingResult's "headline" ("Senior SDE @ Acme") into
    // title / company when the "@" separator is present — otherwise
    // stash the whole thing in current_title so the card still shows
    // something useful in the candidates list.
    let currentTitle: string | null = null;
    let currentCompany: string | null = null;
    if (input.headline && input.headline.trim()) {
      const parts = input.headline.split(/\s+@\s+|\s+at\s+/i).map((s) => s.trim());
      currentTitle = parts[0] || input.headline.trim();
      currentCompany = parts[1] ?? null;
    }

    // linkedin_url only when the source is LinkedIn — other connectors
    // store the canonical profile link in profile_structured_json so we
    // don't pollute the linkedin_url column with non-LinkedIn URLs.
    const linkedinUrl = input.externalSource === 'linkedin' ? input.profileUrl ?? null : null;
    const profileStructured: Record<string, unknown> = {
      external: {
        source: input.externalSource,
        id: input.externalId,
        profileUrl: input.profileUrl ?? null,
        avatarUrl: input.avatarUrl ?? null,
        savedByAgentAt: new Date().toISOString(),
      },
    };

    const payload = {
      org_id: input.orgId,
      created_by: input.createdByOrgUserId ?? '',
      full_name: input.name,
      email: '',
      phone: null,
      linkedin_url: linkedinUrl,
      source_channel: 'api',
      source: 'api',
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      current_company: currentCompany,
      current_title: currentTitle,
      location: input.locationText ?? null,
      avatar_url: input.avatarUrl ?? null,
      base_resume_url: null,
      recruiter_rating: null,
      parsed_skills: input.skills ?? [],
      years_exp: null,
      education: null,
      tags: [],
      notes: null,
      is_active: true,
      sourced_by_agent: true,
      external_source: input.externalSource,
      external_id: input.externalId,
      profile_structured_json: profileStructured,
    } as unknown as Omit<Candidate, 'id' | 'created_at' | 'updated_at'>;

    const { data, error } = await supabase
      .from('ggj_b2b_candidates')
      .insert(payload)
      .select()
      .single();
    if (error) {
      // Race: another tab saved the same (source, id) between the probe
      // and the insert. Re-read the row and treat it as existing.
      if (error.code === '23505' || /duplicate/i.test(error.message)) {
        const { data: raced } = await supabase
          .from('ggj_b2b_candidates')
          .select('*')
          .eq('org_id', input.orgId)
          .eq('external_source', input.externalSource)
          .eq('external_id', input.externalId)
          .maybeSingle();
        if (raced) return { action: 'existing', candidate: raced as Candidate };
      }
      throw error;
    }
    return { action: 'created', candidate: data as Candidate };
  },

  async update(id: string, payload: Partial<Candidate>): Promise<Candidate> {
    const { data, error } = await supabase
      .from('ggj_b2b_candidates')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /** CAN-040: find existing candidates with matching email, phone, or similar name */
  async checkDuplicates(
    orgId: string,
    { email, phone, excludeId }: { email?: string; phone?: string; excludeId?: string }
  ): Promise<Candidate[]> {
    if (!email && !phone) return [];
    const filters: string[] = [];
    if (email) filters.push(`email.ilike.${email}`);
    if (phone) filters.push(`phone.ilike.${phone}`);
    let query = supabase
      .from('ggj_b2b_candidates')
      .select('id, full_name, email, phone, current_title, current_company')
      .eq('org_id', orgId)
      .eq('is_active', true)
      .or(filters.join(','))
      .limit(5);
    if (excludeId) query = query.neq('id', excludeId);
    const { data, error } = await query;
    if (error) return [];
    return (data ?? []) as Candidate[];
  },

  async softDelete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_candidates')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * CAN-040: Merge secondary candidate into primary.
   * - Re-points applications that belong to secondary → primary (if no conflict).
   * - Soft-deletes the secondary candidate.
   * - Writes an audit row to ggj_b2b_candidate_merge_log.
   */
  async merge(
    orgId: string,
    primaryId: string,
    secondaryId: string,
    mergedByOrgUserId: string | null,
    mergedFields: Record<string, unknown> = {}
  ): Promise<void> {
    // Re-point applications that belonged to secondary → primary
    // (skip rows where primary already has an application for the same job)
    const { data: secApps } = await supabase
      .from('ggj_b2b_applications')
      .select('id, job_id')
      .eq('org_id', orgId)
      .eq('candidate_id', secondaryId);

    for (const app of secApps ?? []) {
      const { count } = await supabase
        .from('ggj_b2b_applications')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .eq('candidate_id', primaryId)
        .eq('job_id', app.job_id);
      if ((count ?? 0) === 0) {
        await supabase
          .from('ggj_b2b_applications')
          .update({ candidate_id: primaryId })
          .eq('id', app.id);
      }
    }

    // Get secondary info for audit
    const { data: sec } = await supabase
      .from('ggj_b2b_candidates')
      .select('email')
      .eq('id', secondaryId)
      .single();

    // Soft-delete the secondary candidate
    await supabase
      .from('ggj_b2b_candidates')
      .update({ is_active: false })
      .eq('id', secondaryId);

    // Audit log
    await supabase.from('ggj_b2b_candidate_merge_log').insert({
      org_id: orgId,
      primary_id: primaryId,
      secondary_id: secondaryId,
      secondary_email: sec?.email ?? null,
      merged_fields: mergedFields,
      merged_by: mergedByOrgUserId,
    });
  },

  async uploadResume(orgId: string, candidateId: string, file: File): Promise<string> {
    const path = `resumes/${orgId}/${candidateId}/${file.name}`;
    const { error } = await supabase.storage
      .from(B2B_CANDIDATE_RESUME_BUCKET)
      .upload(path, file, { upsert: true });
    if (error) throw error;
    return path;
  },

  async createOrUpdateFromResume(
    orgId: string,
    structured: StructuredResume
  ): Promise<{ candidate: Candidate; action: 'created' | 'merged' }> {
    const dupes = await candidateService.checkDuplicates(orgId, {
      email: structured.email ?? undefined,
      phone: structured.phone ?? undefined,
    });

    const extraFields = {
      profile_structured_json: structured as unknown as Record<string, unknown>,
      total_experience_years: structured.total_experience_years,
      current_ctc: structured.current_ctc,
      expected_ctc: structured.expected_ctc,
      notice_period_days: structured.notice_period_days,
      preferred_locations: structured.location ? [structured.location] : [],
    };

    if (dupes.length > 0) {
      const existing = dupes[0];
      const mergedSkills = Array.from(
        new Set([...(existing.parsed_skills ?? []), ...structured.skills])
      );
      const updated = await candidateService.update(existing.id, {
        ...extraFields,
        parsed_skills: mergedSkills,
        current_title: structured.current_title ?? existing.current_title,
        current_company: structured.current_company ?? existing.current_company,
        years_exp: structured.total_experience_years ?? existing.years_exp,
      });
      return { candidate: updated, action: 'merged' };
    }

    const created = await candidateService.create({
      org_id: orgId,
      created_by: '',
      full_name: structured.name,
      email: structured.email ?? '',
      phone: structured.phone,
      linkedin_url: null,
      source_channel: 'bulk_upload',
      source: 'bulk_upload',
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      current_company: structured.current_company,
      current_title: structured.current_title,
      location: structured.location,
      base_resume_url: null,
      recruiter_rating: null,
      parsed_skills: structured.skills,
      years_exp: structured.total_experience_years,
      education: structured.education ?? null,
      tags: [],
      notes: null,
      is_active: true,
      ...extraFields,
    } as unknown as Omit<Candidate, 'id' | 'created_at' | 'updated_at'>);

    return { candidate: created, action: 'created' };
  },

  /** Square profile image; stored under avatars/{orgId}/{candidateId}/ */
  async uploadAvatar(orgId: string, candidateId: string, file: File): Promise<string> {
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'jpg';
    const safeExt = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? ext : 'jpg';
    const path = `avatars/${orgId}/${candidateId}/profile.${safeExt}`;
    const { error } = await supabase.storage
      .from(B2B_CANDIDATE_RESUME_BUCKET)
      .upload(path, file, { upsert: true });
    if (error) throw error;
    return path;
  },

  /** Public URL pattern — for private buckets use {@link getResumeSignedUrl} or `PrivateStorageAvatar`. */
  getResumeUrl(path: string): string {
    const { data } = supabase.storage.from(B2B_CANDIDATE_RESUME_BUCKET).getPublicUrl(path);
    return data.publicUrl;
  },

  /** Signed URL for private buckets (e.g. inline .txt preview in the app). */
  async getResumeSignedUrl(path: string, expiresInSec = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from(B2B_CANDIDATE_RESUME_BUCKET)
      .createSignedUrl(path, expiresInSec);
    if (error) throw error;
    return data.signedUrl;
  },
};
